/**
 * Portfolio templates registry — Helencia
 *
 * Each template defines:
 *  - CSS variables applied to the portfolio container (--pt-bg, --pt-fg, --pt-accent, etc.)
 *  - Font family used for titles & body
 *  - Hero layout variant (cinematic | magazine | streetwear | artistic | clean)
 *  - Premium flag (true → requires Premium subscription)
 */

export const TEMPLATES = {
  minimal_clean: {
    id: "minimal_clean",
    name: "Minimal Clean",
    description: "Lumineux, aéré, fond crème. Met les images en pleine lumière.",
    premium: false,
    hero: "clean",
    gallery: { layout: "grid", aspect: "aspect-[4/5]" },
    vars: {
      "--pt-bg": "#FFFBF5",
      "--pt-surface": "#FFFFFF",
      "--pt-fg": "#1A1A1A",
      "--pt-muted": "#8B8680",
      "--pt-accent": "#C68E59",
      "--pt-accent-soft": "#FAF3EA",
      "--pt-border": "#EADDC9",
      "--pt-hero-bg": "#FFFBF5",
      "--pt-hero-fg": "#1A1A1A",
      "--pt-card-bg": "#FFFFFF",
      "--pt-quote-bg": "#1A1A1A",
      "--pt-quote-fg": "#FFFFFF",
    },
    fonts: {
      title: "var(--font-serif, 'Cormorant Garamond', serif)",
      body: "var(--font-sans, 'Inter', system-ui, sans-serif)",
    },
  },

  editorial: {
    id: "editorial",
    name: "Editorial Magazine",
    description: "Mise en page éditoriale, titres XXL, superposition image-texte.",
    premium: false,
    hero: "magazine",
    gallery: { layout: "mosaic", aspect: null },
    vars: {
      "--pt-bg": "#F5F1EA",
      "--pt-surface": "#FFFFFF",
      "--pt-fg": "#0F0E0C",
      "--pt-muted": "#5C564E",
      "--pt-accent": "#A26F3E",
      "--pt-accent-soft": "#EDE3D2",
      "--pt-border": "#D9CFBE",
      "--pt-hero-bg": "#0F0E0C",
      "--pt-hero-fg": "#FFFFFF",
      "--pt-card-bg": "#FFFFFF",
      "--pt-quote-bg": "#A26F3E",
      "--pt-quote-fg": "#FFFFFF",
    },
    fonts: {
      title: "var(--font-serif, 'Cormorant Garamond', serif)",
      body: "var(--font-sans, 'Inter', system-ui, sans-serif)",
    },
  },

  luxe: {
    id: "luxe",
    name: "Luxe",
    description: "Noir profond, or, typographie serif élégante. L'essence du luxe.",
    premium: true,
    hero: "cinematic",
    gallery: { layout: "masonry", aspect: null },
    vars: {
      "--pt-bg": "#0A0908",
      "--pt-surface": "#161311",
      "--pt-fg": "#F5EFE0",
      "--pt-muted": "#9C8E76",
      "--pt-accent": "#D4AF37",
      "--pt-accent-soft": "#1F1A14",
      "--pt-border": "#26211B",
      "--pt-hero-bg": "#0A0908",
      "--pt-hero-fg": "#F5EFE0",
      "--pt-card-bg": "#161311",
      "--pt-quote-bg": "#D4AF37",
      "--pt-quote-fg": "#0A0908",
    },
    fonts: {
      title: "'Playfair Display', 'Cormorant Garamond', serif",
      body: "'Inter', system-ui, sans-serif",
    },
  },

  streetwear: {
    id: "streetwear",
    name: "Streetwear",
    description: "Contrasté, énergique, typo bold, animations dynamiques.",
    premium: true,
    hero: "streetwear",
    gallery: { layout: "masonry", aspect: null },
    vars: {
      "--pt-bg": "#0E0E0E",
      "--pt-surface": "#1A1A1A",
      "--pt-fg": "#FAFAFA",
      "--pt-muted": "#888888",
      "--pt-accent": "#FF5722",
      "--pt-accent-soft": "#241410",
      "--pt-border": "#2A2A2A",
      "--pt-hero-bg": "#0E0E0E",
      "--pt-hero-fg": "#FAFAFA",
      "--pt-card-bg": "#1A1A1A",
      "--pt-quote-bg": "#FF5722",
      "--pt-quote-fg": "#FFFFFF",
    },
    fonts: {
      title: "'Bebas Neue', 'Inter', sans-serif",
      body: "'Inter', system-ui, sans-serif",
    },
  },

  artistic: {
    id: "artistic",
    name: "Artistique",
    description: "Couleurs créatives, layout asymétrique, esprit galerie.",
    premium: true,
    hero: "artistic",
    gallery: { layout: "grid", aspect: "aspect-square" },
    vars: {
      "--pt-bg": "#F8F4EC",
      "--pt-surface": "#FFFFFF",
      "--pt-fg": "#1A2B3D",
      "--pt-muted": "#6F7A87",
      "--pt-accent": "#D14E5F",
      "--pt-accent-soft": "#F7E2E5",
      "--pt-border": "#E0D6C6",
      "--pt-hero-bg": "#1A2B3D",
      "--pt-hero-fg": "#F8F4EC",
      "--pt-card-bg": "#FFFFFF",
      "--pt-quote-bg": "#D14E5F",
      "--pt-quote-fg": "#FFFFFF",
    },
    fonts: {
      title: "'Cormorant Garamond', serif",
      body: "'Inter', system-ui, sans-serif",
    },
  },
};

export const TEMPLATE_LIST = Object.values(TEMPLATES);
export const FREE_TEMPLATES = TEMPLATE_LIST.filter((t) => !t.premium).map((t) => t.id);
export const PREMIUM_TEMPLATES = TEMPLATE_LIST.filter((t) => t.premium).map((t) => t.id);
export const DEFAULT_TEMPLATE = "minimal_clean";

/** Map mood selector responses → recommended template id */
export function moodToTemplate({ universe, ambiance, inspiration }) {
  const u = (universe || "").toLowerCase();
  const a = (ambiance || "").toLowerCase();
  const i = (inspiration || "").toLowerCase();

  if (u.includes("luxe") || a.includes("sombre") || i.includes("runway")) return "luxe";
  if (u.includes("street") || a.includes("contrasté") || a.includes("colore")) return "streetwear";
  if (u.includes("artist") || u.includes("créa") || i.includes("gallery") || i.includes("magazine")) return "artistic";
  if (u.includes("minimal") || u.includes("soft") || a.includes("clair") || a.includes("neutre")) return "minimal_clean";
  return "editorial";
}

export function getTemplate(id) {
  return TEMPLATES[id] || TEMPLATES[DEFAULT_TEMPLATE];
}

/** Inline style object for the portfolio container */
export function templateStyle(id) {
  const t = getTemplate(id);
  return {
    ...t.vars,
    "--pt-font-title": t.fonts.title,
    "--pt-font-body": t.fonts.body,
  };
}
